import { useState, useEffect, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import { CONTRACT_ABI } from '../utils/contractABI';
import { CONTRACT_ADDRESSES, USDT_ADDRESSES } from '../utils/constants';

export const useContract = (provider, signer, network) => {
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [packageData, setPackageData] = useState([]);
  const [poolData, setPoolData] = useState(null);
  
  // Cache management
  const cacheRef = useRef({});
  const lastLoadRef = useRef(null);
  const abortRef = useRef(null);

  // Contract addresses
  const contractAddress = network?.chainId === '0x15EB' 
    ? CONTRACT_ADDRESSES.TESTNET 
    : CONTRACT_ADDRESSES.MAINNET;

  const usdtAddress = network?.chainId === '0x15EB'
    ? USDT_ADDRESSES.TESTNET 
    : USDT_ADDRESSES.MAINNET;

  // Contract instances
  const contract = provider && contractAddress ? 
    new ethers.Contract(contractAddress, CONTRACT_ABI, provider) : null;
    
  const contractWithSigner = signer && contractAddress ? 
    new ethers.Contract(contractAddress, CONTRACT_ABI, signer) : null;

  const usdtContract = signer && usdtAddress ? 
    new ethers.Contract(usdtAddress, [
      "function approve(address spender, uint256 amount) external returns (bool)",
      "function allowance(address owner, address spender) external view returns (uint256)",
      "function balanceOf(address account) external view returns (uint256)"
    ], signer) : null;

  // User ID check (cached)
  const getUserId = useCallback(async (address) => {
    if (!contract || !address) return null;

    const cacheKey = `userId_${address}`;
    const cached = cacheRef.current[cacheKey];
    
    // Cache for 60 seconds
    if (cached && Date.now() - cached.timestamp < 60000) {
      return cached.value;
    }

    try {
      const userId = await contract.getUserIdByAddress(address);
      const result = userId && userId.toString() !== '0' ? userId.toString() : null;
      
      // Cache result
      cacheRef.current[cacheKey] = {
        value: result,
        timestamp: Date.now()
      };
      
      return result;
    } catch (error) {
      console.error('getUserId failed:', error);
      return null;
    }
  }, [contract]);

  // Get user details
  const getUserDetails = useCallback(async (userId) => {
    if (!contract || !userId) return null;
    
    try {
      const details = await contract.getUserDetails(userId);
      
      if (!details) return null;
      
      return {
        account: details[0],
        id: details[1].toString(),
        referrer: details[2].toString(),
        isActive: details[3],
        totalDirectReferrals: details[4].toString(),
        claimableAmount: ethers.utils.formatEther(details[5]),
        totalClaimed: ethers.utils.formatEther(details[6]),
        totalIncome: ethers.utils.formatEther(details[7])
      };
    } catch (error) {
      console.error('getUserDetails failed:', error);
      return null;
    }
  }, [contract]);

  // Get user packages
  const getUserPackages = useCallback(async (userId) => {
    if (!contract || !userId) return [];
    
    try {
      const packages = [];
      
      // Load packages in parallel
      const packagePromises = Array.from({ length: 7 }, (_, i) => 
        contract.getUserPackageDetails(userId, i + 1)
      );
      
      const packageResults = await Promise.all(packagePromises);
      
      packageResults.forEach((pkg, index) => {
        if (pkg) {
          packages.push({
            id: index + 1,
            name: `U${index + 1}`,
            isActive: pkg[0],
            activationTime: pkg[1].toString(),
            totalInvested: ethers.utils.formatEther(pkg[2]),
            totalEarned: ethers.utils.formatEther(pkg[3]),
            ceilingLimit: ethers.utils.formatEther(pkg[4]),
            remainingLimit: ethers.utils.formatEther(pkg[5])
          });
        } else {
          packages.push({
            id: index + 1,
            name: `U${index + 1}`,
            isActive: false,
            activationTime: '0',
            totalInvested: '0',
            totalEarned: '0',
            ceilingLimit: '0',
            remainingLimit: '0'
          });
        }
      });
      
      return packages;
    } catch (error) {
      console.error('getUserPackages failed:', error);
      return [];
    }
  }, [contract]);

  // Get pool data
  const getPoolData = useCallback(async (userId) => {
    if (!contract || !userId) return null;
    
    try {
      const [eligibilities, claimableAmounts] = await Promise.all([
        contract.getPoolEligibilities(userId),
        contract.getUserClaimablePoolIncomes(userId)
      ]);
      
      if (!eligibilities || !claimableAmounts) return null;
      
      return {
        galaxy: {
          eligible: eligibilities[0],
          claimable: ethers.utils.formatEther(claimableAmounts[0])
        },
        cosmos: {
          eligible: eligibilities[1],
          claimable: ethers.utils.formatEther(claimableAmounts[1])
        },
        universe: {
          eligible: eligibilities[2],
          claimable: ethers.utils.formatEther(claimableAmounts[2])
        },
        eternal: {
          eligible: eligibilities[3],
          claimable: ethers.utils.formatEther(claimableAmounts[3])
        }
      };
    } catch (error) {
      console.error('getPoolData failed:', error);
      return null;
    }
  }, [contract]);

  // Get income breakdown
  const getIncomeBreakdown = useCallback(async (userId) => {
    if (!contract || !userId) return null;
    
    try {
      const breakdown = await contract.getUserIncomeBreakdown(userId);
      
      if (!breakdown) return null;
      
      return {
        sponsorIncome: ethers.utils.formatEther(breakdown[0]),
        levelIncome: ethers.utils.formatEther(breakdown[1]),
        galaxyPoolIncome: ethers.utils.formatEther(breakdown[2]),
        cosmosPoolIncome: ethers.utils.formatEther(breakdown[3]),
        universePoolIncome: ethers.utils.formatEther(breakdown[4]),
        eternalPoolIncome: ethers.utils.formatEther(breakdown[5]),
        totalIncome: ethers.utils.formatEther(breakdown[6]),
        claimableAmount: ethers.utils.formatEther(breakdown[7]),
        totalClaimed: ethers.utils.formatEther(breakdown[8])
      };
    } catch (error) {
      console.error('getIncomeBreakdown failed:', error);
      return null;
    }
  }, [contract]);

  // Load user data
  const loadUserData = useCallback(async (address, forceRefresh = false) => {
    if (!address || !contract) return;
    
    // Prevent duplicate calls
    if (!forceRefresh && lastLoadRef.current === address && Date.now() - (cacheRef.current.lastLoad || 0) < 30000) {
      return;
    }

    // Cancel previous request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    console.log('Loading user data:', address);
    setLoading(true);
    lastLoadRef.current = address;
    cacheRef.current.lastLoad = Date.now();

    try {
      // Get user ID first
      const userId = await getUserId(address);
      
      if (!userId) {
        setUserData(null);
        setPackageData([]);
        setPoolData(null);
        return;
      }

      // Load essential data in parallel
      const [userDetails, userPackages, incomeBreakdown] = await Promise.all([
        getUserDetails(userId),
        getUserPackages(userId),
        getIncomeBreakdown(userId)
      ]);

      if (abortRef.current?.signal.aborted) return;

      // Combine user details and income data
      const combinedUserData = userDetails && incomeBreakdown ? 
        { ...userDetails, ...incomeBreakdown } : userDetails;
      
      setUserData(combinedUserData);
      setPackageData(userPackages || []);

      // Load pool data after a short delay
      setTimeout(async () => {
        try {
          const poolInfo = await getPoolData(userId);
          setPoolData(poolInfo);
        } catch (error) {
          console.warn('Pool data loading failed:', error);
        }
      }, 1000);

    } catch (error) {
      console.error('Load user data failed:', error);
      setUserData(null);
      setPackageData([]);
      setPoolData(null);
    } finally {
      setLoading(false);
    }
  }, [contract, getUserId, getUserDetails, getUserPackages, getIncomeBreakdown, getPoolData]);

  // Refresh function
  const refreshUserData = useCallback(async (address) => {
    if (address) {
      lastLoadRef.current = null;
      cacheRef.current.lastLoad = 0;
      await loadUserData(address, true);
    }
  }, [loadUserData]);

  // Transaction functions
  const register = useCallback(async (referrerId) => {
    if (!contractWithSigner || !usdtContract) throw new Error('Wallet not connected');
    
    setLoading(true);
    try {
      const packagePrice = ethers.utils.parseEther('10');
      
      console.log('Registering...');
      
      // Approve USDT
      const approveTx = await usdtContract.approve(contractAddress, packagePrice);
      await approveTx.wait(1);
      
      // Register
      const tx = await contractWithSigner.register(referrerId || 100000);
      await tx.wait(1);
      
      return tx;
    } finally {
      setLoading(false);
    }
  }, [contractWithSigner, usdtContract, contractAddress]);

  const upgradePackage = useCallback(async (packageId) => {
    if (!contractWithSigner || !usdtContract) throw new Error('Wallet not connected');
    
    setLoading(true);
    try {
      const packagePrices = ['10', '20', '40', '80', '150', '250', '450'];
      const packagePrice = ethers.utils.parseEther(packagePrices[packageId - 1]);
      
      // Approve USDT
      const approveTx = await usdtContract.approve(contractAddress, packagePrice);
      await approveTx.wait(1);
      
      // Upgrade
      const tx = await contractWithSigner.upgradePackage(packageId);
      await tx.wait(1);
      
      return tx;
    } finally {
      setLoading(false);
    }
  }, [contractWithSigner, usdtContract, contractAddress]);

  const retopupPackage = useCallback(async (packageId) => {
    if (!contractWithSigner || !usdtContract) throw new Error('Wallet not connected');
    
    setLoading(true);
    try {
      const packagePrices = ['10', '20', '40', '80', '150', '250', '450'];
      const packagePrice = ethers.utils.parseEther(packagePrices[packageId - 1]);
      
      // Approve USDT
      const approveTx = await usdtContract.approve(contractAddress, packagePrice);
      await approveTx.wait(1);
      
      // Retopup
      const tx = await contractWithSigner.retopupPackage(packageId);
      await tx.wait(1);
      
      return tx;
    } finally {
      setLoading(false);
    }
  }, [contractWithSigner, usdtContract, contractAddress]);

  const claimIncome = useCallback(async () => {
    if (!contractWithSigner) throw new Error('Wallet not connected');
    
    setLoading(true);
    try {
      const tx = await contractWithSigner.claimIncome();
      await tx.wait(1);
      return tx;
    } finally {
      setLoading(false);
    }
  }, [contractWithSigner]);

  const claimAllPoolIncomes = useCallback(async () => {
    if (!contractWithSigner) throw new Error('Wallet not connected');
    
    setLoading(true);
    try {
      const tx = await contractWithSigner.claimAllPoolIncomes();
      await tx.wait(1);
      return tx;
    } finally {
      setLoading(false);
    }
  }, [contractWithSigner]);

  // Admin functions
  const adminRegister = useCallback(async (userAddress, referrerId) => {
    if (!contractWithSigner) throw new Error('Wallet not connected');
    
    setLoading(true);
    try {
      const tx = await contractWithSigner.adminRegister(userAddress, referrerId || 100000);
      await tx.wait();
      return tx;
    } finally {
      setLoading(false);
    }
  }, [contractWithSigner]);

  const adminUpgradePackage = useCallback(async (userId, packageId) => {
    if (!contractWithSigner) throw new Error('Wallet not connected');
    
    setLoading(true);
    try {
      const tx = await contractWithSigner.adminUpgradePackage(userId, packageId);
      await tx.wait();
      return tx;
    } finally {
      setLoading(false);
    }
  }, [contractWithSigner]);

  const adminRetopupPackage = useCallback(async (userId, packageId) => {
    if (!contractWithSigner) throw new Error('Wallet not connected');
    
    setLoading(true);
    try {
      const tx = await contractWithSigner.adminRetopupPackage(userId, packageId);
      await tx.wait();
      return tx;
    } finally {
      setLoading(false);
    }
  }, [contractWithSigner]);

  // Additional functions for admin/team components
  const getContractStats = useCallback(async () => {
    if (!contract) return null;
    
    try {
      const [totalUsers, reservePool] = await Promise.all([
        contract.getTotalUsers(),
        contract.getReservePoolBalance()
      ]);
      
      if (!totalUsers || !reservePool) return null;
      
      return {
        totalUsers: totalUsers.toString(),
        reservePool: ethers.utils.formatEther(reservePool)
      };
    } catch (error) {
      console.error('getContractStats failed:', error);
      return null;
    }
  }, [contract]);

  const getPoolAccumulations = useCallback(async () => {
    if (!contract) return null;
    
    try {
      const accumulations = await contract.getAllPoolAccumulations();
      
      if (!accumulations) return null;
      
      return {
        galaxy: ethers.utils.formatEther(accumulations[0]),
        cosmos: ethers.utils.formatEther(accumulations[1]),
        universe: ethers.utils.formatEther(accumulations[2]),
        eternal: ethers.utils.formatEther(accumulations[3])
      };
    } catch (error) {
      console.error('getPoolAccumulations failed:', error);
      return null;
    }
  }, [contract]);

  const getUserReferrals = useCallback(async (userId) => {
    if (!contract || !userId) return [];
    
    try {
      const referralIds = await contract.getUserReferrals(userId);
      
      if (!referralIds || referralIds.length === 0) return [];
      
      // Limit to first 50 for performance
      const limitedIds = referralIds.slice(0, 50);
      const referrals = [];
      
      for (const refId of limitedIds) {
        try {
          const details = await getUserDetails(refId.toString());
          if (details) {
            referrals.push(details);
          }
        } catch (err) {
          console.warn('Failed to load referral:', refId.toString());
        }
      }
      
      return referrals;
    } catch (error) {
      console.error('getUserReferrals failed:', error);
      return [];
    }
  }, [contract, getUserDetails]);

  const getMatrixData = useCallback(async (userId, packageId) => {
    if (!contract || !userId) {
      return { upline: '0', directDownlines: [], totalTeamSize: '0' };
    }
    
    try {
      const matrixDetails = await contract.getMatrixDetails(userId, packageId);
      
      if (!matrixDetails) {
        return { upline: '0', directDownlines: [], totalTeamSize: '0' };
      }
      
      return {
        upline: matrixDetails[0].toString(),
        directDownlines: matrixDetails[1].map(id => id.toString()).filter(id => id !== '0'),
        totalTeamSize: matrixDetails[2].toString()
      };
    } catch (error) {
      console.error('getMatrixData failed:', error);
      return { upline: '0', directDownlines: [], totalTeamSize: '0' };
    }
  }, [contract]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  return {
    // Contract instances
    contract,
    contractWithSigner,
    
    // State
    loading,
    userData,
    packageData,
    poolData,
    
    // Core functions
    loadUserData,
    refreshUserData,
    getUserId,
    getUserDetails,
    getUserPackages,
    getIncomeBreakdown,
    getPoolData,
    getUserReferrals,
    getMatrixData,
    getContractStats,
    getPoolAccumulations,
    
    // Transaction functions
    register,
    upgradePackage,
    retopupPackage,
    claimIncome,
    claimAllPoolIncomes,
    
    // Admin functions
    adminRegister,
    adminUpgradePackage,
    adminRetopupPackage
  };
};